// import external libraries
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

// import services
import { mutations } from 'RestAPIs/rapini';
import showSnackbar, { showSuccessSnackbar } from 'components/shared/Snackbar';

// import queries
import { refreshBacklogForEntities } from '../../Backlog.queries';

// import stores
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';

// import utils
import { createAppointmentFormDataFromEventResponse } from 'components/mrp/components/Calendar/components/CalendarAppointment/CalenderAppointment.utils';
import { convertFormFieldDataToPayload } from 'components/shared/DialogProvider/dialogs/SmartActionDialog/SmartActionDialog.utils';
import { AppointmentRecurrenceType } from 'components/mrp/components/AppointmentForm/AppointmentForm.types';

// import types
import type { AppointmentFormFieldData } from 'components/mrp/components/AppointmentForm/AppointmentForm.types';

// import types
import type {
  EditAppointmentTypeResponse,
  EventResponse,
  PlanningEntityType,
  SchedulingActionResultResponse
} from 'generated/index';
import type { OutsideTreatmentAction } from './OutsideTreatmentAppointmentCard.types';

// The planning-entity types that appear as their own participant list on an appointment form, so an entity
// can be dropped from one. The generated PlanningEntityType union is wider than this.
const PARTICIPANT_KEYS = [
  'CLIENT',
  'EMPLOYEE',
  'RESOURCE',
  'WORKPLACE'
] as const;
type ParticipantKey = (typeof PARTICIPANT_KEYS)[number];
const isParticipantKey = (type: PlanningEntityType): type is ParticipantKey =>
  (PARTICIPANT_KEYS as readonly string[]).includes(type);

/**
 * The delete and unassign actions on an outside-treatment card, wired to the endpoints afsprakenplanning
 * uses: `deleteAppointment` for the delete, and `updateAppointment` for the unassign — the same way
 * afsprakenplanning's own "entiteit loskoppelen" (EntityQuickDisconnect) works: rebuild the appointment
 * from the full event minus the section's own entity and save it through the update endpoint. The backlog
 * event carries every participant (the card renders them), so dropping one entity removes only that entity
 * and leaves the other care receivers, employees, resources and workplace in place. Which participant list
 * it is dropped from depends on {@link entityType} — a client under a care-receiver/overlap section, but an
 * employee, resource or workplace under their own overlap sections.
 * A one-off appointment is changed straight away; a recurring one first asks whether to change only this
 * occurrence or this and all following ones (surfaced through `pendingAction` + `confirmRecurrence`),
 * passed through as the `editAppointmentType`. On success the backlog is refreshed so the card drops out.
 */
export const useOutsideTreatmentAppointmentActions = (
  event: EventResponse,
  careReceiverId: string,
  entityType: PlanningEntityType = 'CLIENT'
) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [pendingAction, setPendingAction] =
    useState<OutsideTreatmentAction | null>(null);
  // The notification texts a rejected-with-warnings update returned, shown in the confirm dialog; and the
  // rebuilt appointment kept aside so confirming can re-send it with warnings suppressed.
  const [notificationMessages, setNotificationMessages] = useState<
    string[] | null
  >(null);
  const [pendingUnassignData, setPendingUnassignData] =
    useState<AppointmentFormFieldData | null>(null);

  const onError = () => {
    showSnackbar({ severity: 'error', message: t('somethingWentWrong') });
  };

  // Refresh only the entities the change touched — not the whole backlog. The success toast waits for the
  // targeted refetch, so a failed refresh surfaces the error toast instead of a false "gelukt" and never
  // leaves an unhandled promise rejection behind a stale list.
  const refreshBacklog = async (affectedEntityIds: string[]) => {
    try {
      await refreshBacklogForEntities(queryClient, affectedEntityIds);
      showSuccessSnackbar();
    } catch {
      onError();
    }
  };

  // Delete affects everyone on the appointment, and `deleteAppointment` returns only a string, so the
  // affected entities are the appointment's own participants (care receivers, employees, resources and
  // its workplace).
  const appointmentEntityIds = [
    ...(event.clients ?? []).map((client) => client.id),
    ...(event.employees ?? []).map((employee) => employee.entityId),
    ...(event.resources ?? []).map((resource) => resource.id),
    event.workPlace?.entityId
  ].filter((id): id is string => !!id);

  const onDeleteSuccess = () => refreshBacklog(appointmentEntityIds);

  // The removed entity is no longer in the update response, so it is added explicitly to the refresh —
  // its row is the one that must drop out.
  const refreshAfterUnassign = (data: SchedulingActionResultResponse) =>
    refreshBacklog([
      careReceiverId,
      ...(data.planningEntityIdentifiers ?? [])
        .map((identifier) => identifier.entityId)
        .filter((id): id is string => !!id)
    ]);

  const { mutate: deleteAppointment } = mutations.useDeleteAppointment({
    onSuccess: onDeleteSuccess,
    onError
  });
  // mutateAsync, not mutate: the notification response has to be handled inline so a first attempt can be
  // told apart from a suppressed retry (a shared onSuccess cannot).
  const { mutateAsync: updateAppointment } = mutations.useUpdateAppointment();

  // Exactly the delete payload afsprakenplanning sends: the occurrence identifier and the edit scope.
  const runDelete = (editAppointmentType: EditAppointmentTypeResponse) =>
    deleteAppointment({
      appointmentOccurrenceIdentifier: {
        entityId: event.id,
        originalOccurrenceStart: dayjs(event.start).format('YYYY-MM-DD')
      },
      editAppointmentType
    });

  // Remove only the section's own entity: rebuild the appointment from the full event with this entity
  // dropped from the participant list of its own type — a client under a care-receiver section, an
  // employee/resource/workplace under their overlap sections. createAppointmentFormDataFromEventResponse
  // carries every other participant and the occurrence start, so the update keeps them and removes only
  // this entity (afsprakenplanning's EntityQuickDisconnect does the same).
  const buildUnassignFormData = (
    editAppointmentType: EditAppointmentTypeResponse
  ): AppointmentFormFieldData | null => {
    const formData = createAppointmentFormDataFromEventResponse(event);
    if (!formData) return null;
    // entityType is always one of the four the form carries (CLIENT/EMPLOYEE/RESOURCE/WORKPLACE), but the
    // generated PlanningEntityType union is wider; narrow before indexing planEntities so an unexpected
    // type leaves the participants untouched rather than failing to type-check.
    const removalKey = isParticipantKey(entityType) ? entityType : null;
    return {
      ...formData,
      planEntities: removalKey
        ? {
            ...formData.planEntities,
            [removalKey]: (formData.planEntities[removalKey] ?? []).filter(
              (participant) => participant.entityId !== careReceiverId
            )
          }
        : formData.planEntities,
      // The card only ever asks for these two scopes; map the response type onto the form's enum.
      editAppointmentType:
        editAppointmentType === 'THIS_AND_ALL_SUBSEQUENT'
          ? AppointmentRecurrenceType.THIS_AND_ALL_SUBSEQUENT
          : AppointmentRecurrenceType.ONLY_THIS
    };
  };

  // Save the rebuilt appointment. The backend can answer HTTP 200 with `success: false` and a list of
  // notifications (e.g. "employee outside working hours") — a soft block, not an error: surface those and
  // let the planner decide, exactly as afsprakenplanning does. On confirm the same edit is re-sent with
  // warnings suppressed. A rejection without notifications, or a suppressed retry that still fails, is a
  // real error.
  //
  // Only an explicit `success: true` counts as accepted: `success` is optional in the contract, so a
  // response without it must not fall through to the success path and report "gelukt" for an edit that
  // never landed. A missing `data` throws on `data.notifications` inside this try and lands on onError().
  const submitUnassign = async (
    unassignData: AppointmentFormFieldData,
    suppressWarnings: boolean
  ) => {
    try {
      const data = await updateAppointment(
        convertFormFieldDataToPayload(unassignData, suppressWarnings)
      );
      if (data?.success !== true) {
        const messages = (data.notifications ?? [])
          .map((notification) => notification.text)
          .filter((text): text is string => !!text);
        if (messages.length && !suppressWarnings) {
          setPendingUnassignData(unassignData);
          setNotificationMessages(messages);
          return;
        }
        onError();
        return;
      }
      setNotificationMessages(null);
      setPendingUnassignData(null);
      await refreshAfterUnassign(data);
    } catch {
      onError();
    }
  };

  const runUnassign = (editAppointmentType: EditAppointmentTypeResponse) => {
    const unassignData = buildUnassignFormData(editAppointmentType);
    if (!unassignData) return;
    submitUnassign(unassignData, false);
  };

  // Re-send the pending edit with warnings suppressed once the planner accepts the notifications.
  const confirmNotifications = () => {
    if (!pendingUnassignData) return;
    setNotificationMessages(null);
    submitUnassign(pendingUnassignData, true);
  };

  const cancelNotifications = () => {
    setNotificationMessages(null);
    setPendingUnassignData(null);
  };

  // A recurring appointment opens the prompt; a single one is carried out at once with ONLY_THIS.
  const startAction = (action: OutsideTreatmentAction) => {
    if (event.isRecurring) {
      setPendingAction(action);
      return;
    }
    if (action === 'delete') {
      runDelete('ONLY_THIS');
    } else {
      runUnassign('ONLY_THIS');
    }
  };

  const confirmRecurrence = (
    editAppointmentType: EditAppointmentTypeResponse
  ) => {
    if (pendingAction === 'delete') {
      runDelete(editAppointmentType);
    } else if (pendingAction === 'unassign') {
      runUnassign(editAppointmentType);
    }
    setPendingAction(null);
  };

  const cancelRecurrence = () => setPendingAction(null);

  // Edit opens afsprakenplanning's own appointment drawer, reused in the takenlijst: write the event into
  // the shared appointment store as form data and the mounted AppointmentFormVisibility shows the drawer.
  // The save (and its recurrence prompt) is owned by that form/SmartActionDialog, not by this hook; the
  // takenlijst refreshes itself when the drawer closes (see useRefreshBacklogOnAppointmentClose).
  // Two scalars rather than the appointment itself: this hook runs once per card, the store is app-wide and
  // is written on every keystroke in the drawer, and subscribing to the object would re-render every card in
  // the week detail per character. Only these two are ever read.
  const hasOpenAppointment = useAppointmentStore(
    (state) => !!state.appointment
  );
  const openAppointmentId = useAppointmentStore(
    (state) => state.appointment?.id
  );
  const isAppointmentDirty = useAppointmentStore((state) => state.dirty);
  const setAppointmentData = useAppointmentStore(
    (state) => state.control.setAppointmentData
  );
  const closeAppointment = useAppointmentStore(
    (state) => state.control.closeAppointment
  );
  const onEdit = () => {
    const formData = createAppointmentFormDataFromEventResponse(event);
    if (!formData) return;
    // setAppointmentData refuses to overwrite an appointment that is already open, and the store is
    // persisted — so a drawer left open in afsprakenplanning (or left dirty there, which blocks its close)
    // survives the navigation and would make this action do nothing at all, with no drawer and no message.
    // Handle that rather than fail silently: an untouched leftover is closed first, an edited one is kept
    // and the planner is told why nothing opened.
    if (hasOpenAppointment) {
      if (openAppointmentId === formData.id) return;
      if (isAppointmentDirty) {
        showSnackbar({
          severity: 'warning',
          message: t('backlog.appointmentAlreadyOpen')
        });
        return;
      }
      closeAppointment(true);
    }
    setAppointmentData(formData);
  };

  return {
    onDelete: () => startAction('delete'),
    onUnassign: () => startAction('unassign'),
    onEdit,
    pendingAction,
    confirmRecurrence,
    cancelRecurrence,
    notificationMessages,
    confirmNotifications,
    cancelNotifications
  };
};
